import { motion } from "framer-motion";
import {
  Music2,
  Rocket,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  User,
  Settings,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const navItems = [
  { id: "studio", label: "The Studio", icon: Music2, description: "Creation & Asset Vault" },
  { id: "engine", label: "Content Engine", icon: Rocket, description: "Automation & Rendering" },
  { id: "business", label: "The Business", icon: BarChart3, description: "Analytics & Distribution" },
];

export const Sidebar = ({ activeTab, setActiveTab, expanded, setExpanded }) => {
  return (
    <TooltipProvider delayDuration={200}>
      <motion.aside
        initial={false}
        animate={{ width: expanded ? 260 : 80 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed left-0 top-0 h-screen bg-meta-surface border-r border-meta-border z-50 flex flex-col"
        data-testid="sidebar"
      >
        {/* Logo / Brand */}
        <div className="p-4 border-b border-meta-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-meta-neon-dim border border-meta-neon flex items-center justify-center">
              <span className="text-meta-neon font-mono font-bold text-lg">E</span>
            </div>
            {expanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="overflow-hidden"
              >
                <h1 className="font-sans font-bold text-meta-text text-lg tracking-tight">MetaDash</h1>
                <p className="text-meta-text-muted text-xs font-mono">v1.0.0</p>
              </motion.div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4" data-testid="sidebar-navigation">
          <div className="space-y-1 px-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;

              return (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-3 transition-all duration-200 border-l-2 ${
                        isActive
                          ? "bg-meta-neon-dim text-meta-neon border-l-meta-neon"
                          : "text-meta-text-secondary hover:text-meta-text hover:bg-meta-base border-l-transparent"
                      }`}
                      data-testid={`sidebar-${item.id}-tab`}
                    >
                      <Icon size={20} strokeWidth={isActive ? 2 : 1.5} />
                      {expanded && (
                        <motion.div
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="text-left overflow-hidden"
                        >
                          <span className="block font-medium text-sm">{item.label}</span>
                          <span className="block text-xs text-meta-text-muted font-mono truncate">
                            {item.description}
                          </span>
                        </motion.div>
                      )}
                    </button>
                  </TooltipTrigger>
                  {!expanded && (
                    <TooltipContent side="right" className="bg-meta-elevated border-meta-border">
                      <p className="font-medium">{item.label}</p>
                      <p className="text-xs text-meta-text-muted">{item.description}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              );
            })}
          </div>
        </nav>

        {/* Artist Profile */}
        <div className="border-t border-meta-border p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-meta-elevated border border-meta-border flex items-center justify-center">
              <User size={18} className="text-meta-text-secondary" />
            </div>
            {expanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex-1 overflow-hidden"
              >
                <p className="font-medium text-sm text-meta-text truncate">OGTommyP</p>
                <p className="text-xs text-meta-neon font-mono">Artist</p>
              </motion.div>
            )}
          </div>
        </div>

        {/* Collapse Toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-meta-surface border border-meta-border flex items-center justify-center hover:border-meta-neon hover:text-meta-neon transition-colors"
          data-testid="sidebar-toggle"
        >
          {expanded ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </button>
      </motion.aside>
    </TooltipProvider>
  );
};
